import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../utils/api';
import { UserCog, Building2, X } from 'lucide-react';

export const emptyAssignee = {
  default_assignee_type: '',
  default_worker_id: '',
  default_partner_company_id: '',
  default_assignee_name: '',
  default_assignee_email: '',
};

export function assigneeFromUnit(unit = {}) {
  return {
    default_assignee_type: unit.default_assignee_type || '',
    default_worker_id:
      unit.default_worker_id != null && unit.default_worker_id !== ''
        ? String(unit.default_worker_id)
        : '',
    default_partner_company_id:
      unit.default_partner_company_id != null && unit.default_partner_company_id !== ''
        ? String(unit.default_partner_company_id)
        : '',
    default_assignee_name:
      unit.default_assignee_name || unit.default_worker_name || unit.partner_company_name || '',
    default_assignee_email: unit.default_assignee_email || '',
  };
}

export function formatAssigneeChip(assignee, { compact = false } = {}) {
  const name =
    assignee?.default_assignee_name ||
    assignee?.default_worker_name ||
    (assignee?.default_worker_id ? `Worker #${assignee.default_worker_id}` : '');
  const hasType = !!assignee?.default_assignee_type;
  const hasId = !!(assignee?.default_worker_id || assignee?.default_partner_company_id);
  if (!name && !hasType && !hasId) {
    return { label: compact ? 'IQP / co.' : 'Set default IQP / company', empty: true };
  }
  const kind =
    assignee.default_assignee_type === 'EXTERNAL_COMPANY'
      ? 'Ext'
      : assignee.default_assignee_type === 'INTERNAL_IQP'
        ? 'IQP'
        : '';
  const display = name || 'Unnamed';
  if (compact) {
    return { label: kind ? `${display} · ${kind}` : display, empty: false };
  }
  const kindFull =
    assignee.default_assignee_type === 'EXTERNAL_COMPANY'
      ? 'External'
      : assignee.default_assignee_type === 'INTERNAL_IQP'
        ? 'IQP'
        : '';
  return { label: kindFull ? `${display} · ${kindFull}` : display, empty: false };
}

/**
 * Top-right chip: click to open edit dialog for default internal IQP or external company.
 * onSave(assigneeFields) ??parent persists (local form or API).
 */
export default function UnitDefaultAssignee({
  value,
  onSave,
  saving = false,
  compact = false,
  /** 'ss' = whole specified system; omit for legacy unit wording */
  scope = 'unit',
  ssCode = '',
}) {
  const [open, setOpen] = useState(false);
  const chip = formatAssigneeChip(value, { compact });

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        title={
          scope === 'ss'
            ? 'Default IQP / company for this entire SS (all units)'
            : 'Click to set or change default responsible IQP / company'
        }
        className={`inline-flex items-center gap-1 max-w-[12rem] rounded-md font-medium border transition-colors ${
          compact ? 'px-1.5 py-0.5 text-[10px] leading-tight' : 'gap-1.5 px-2.5 py-1 text-xs rounded-lg max-w-[14rem]'
        } ${
          chip.empty
            ? 'border-dashed border-gray-300 text-gray-500 hover:border-primary-400 hover:text-primary-700'
            : 'border-primary-200 bg-primary-50 text-primary-800 hover:bg-primary-100'
        }`}
      >
        {value?.default_assignee_type === 'EXTERNAL_COMPANY' ? (
          <Building2 className={compact ? 'w-3 h-3 shrink-0' : 'w-3.5 h-3.5 shrink-0'} />
        ) : (
          <UserCog className={compact ? 'w-3 h-3 shrink-0' : 'w-3.5 h-3.5 shrink-0'} />
        )}
        <span className="truncate">{chip.label}</span>
      </button>

      {open &&
        createPortal(
          <AssigneeEditDialog
            initial={value || emptyAssignee}
            saving={saving}
            scope={scope}
            ssCode={ssCode}
            onClose={() => setOpen(false)}
            onSave={async (next) => {
              await onSave(next);
              setOpen(false);
            }}
          />,
          document.body
        )}
    </>
  );
}

function AssigneeEditDialog({ initial, onClose, onSave, saving, scope = 'unit', ssCode = '' }) {
  const [draft, setDraft] = useState(() => ({ ...emptyAssignee, ...initial }));

  const { data: workers = [] } = useQuery({
    queryKey: ['workers', 'assignee-picker'],
    queryFn: async () => {
      const res = await api.get('/workers', { params: { limit: 500, status: 'active' } });
      return res.data.data || [];
    },
  });

  const { data: partners = [] } = useQuery({
    queryKey: ['partner-companies', 'assignee-picker'],
    queryFn: async () => {
      const res = await api.get('/partner-companies');
      return res.data.data || [];
    },
  });

  useEffect(() => {
    setDraft({ ...emptyAssignee, ...initial });
  }, [initial]);

  const applyWorker = (workerId) => {
    const w = workers.find((x) => String(x.id) === String(workerId));
    setDraft((prev) => ({
      ...prev,
      default_assignee_type: 'INTERNAL_IQP',
      default_worker_id: workerId || '',
      default_partner_company_id: '',
      default_assignee_name: w?.name || '',
      default_assignee_email: w?.email || '',
    }));
  };

  const applyPartner = (partnerId) => {
    const p = partners.find((x) => String(x.id) === String(partnerId));
    setDraft((prev) => ({
      ...prev,
      default_assignee_type: 'EXTERNAL_COMPANY',
      default_partner_company_id: partnerId || '',
      default_worker_id: '',
      default_assignee_name: p?.name || p?.iqp_name || '',
      default_assignee_email: p?.email || '',
    }));
  };

  const handleClear = () => {
    setDraft({ ...emptyAssignee });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const payload = {
      default_assignee_type: draft.default_assignee_type || null,
      default_worker_id:
        draft.default_assignee_type === 'INTERNAL_IQP' ? draft.default_worker_id || null : null,
      default_partner_company_id:
        draft.default_assignee_type === 'EXTERNAL_COMPANY'
          ? draft.default_partner_company_id
            ? Number(draft.default_partner_company_id)
            : null
          : null,
      default_assignee_name: draft.default_assignee_name || null,
      default_assignee_email: draft.default_assignee_email || null,
    };
    onSave(payload);
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-card rounded-xl shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              {scope === 'ss' ? 'Default IQP for this SS' : 'Default responsible party'}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {scope === 'ss' ? (
                <>
                  Applies to <strong>all units</strong> under{' '}
                  <strong>{ssCode || 'this specified system'}</strong> on this building ??notifications,
                  Order/Inspections, and job defaults.
                </>
              ) : (
                <>
                  Internal IQP or external company for this SS unit. After save, they are notified by email (app
                  portfolio later).
                </>
              )}
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-4 space-y-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={draft.default_assignee_type || ''}
              onChange={(e) => {
                const t = e.target.value;
                setDraft((prev) => ({
                  ...emptyAssignee,
                  default_assignee_type: t,
                }));
              }}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">— Not set —</option>
              <option value="INTERNAL_IQP">Internal IQP</option>
              <option value="EXTERNAL_COMPANY">External company</option>
            </select>
          </div>

          {draft.default_assignee_type === 'INTERNAL_IQP' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">IQP / worker</label>
              <select
                value={draft.default_worker_id || ''}
                onChange={(e) => applyWorker(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                required
              >
                <option value="">Select worker…</option>
                {workers.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                    {w.email ? ` (${w.email})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {draft.default_assignee_type === 'EXTERNAL_COMPANY' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Partner company</label>
              <select
                value={draft.default_partner_company_id || ''}
                onChange={(e) => applyPartner(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                required
              >
                <option value="">Select company…</option>
                {partners.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {p.email ? ` (${p.email})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {(draft.default_assignee_name || draft.default_assignee_email) && (
            <p className="text-xs text-gray-600 bg-gray-50 rounded-lg px-3 py-2">
              Will notify: <strong>{draft.default_assignee_name || '—'}</strong>
              {draft.default_assignee_email ? ` · ${draft.default_assignee_email}` : ' · (no email)'}
            </p>
          )}

          <div className="flex justify-between gap-2 pt-1">
            <button
              type="button"
              onClick={handleClear}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
            >
              Clear
            </button>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg text-sm">
                Cancel
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSubmit(e);
                }}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
